import { StoryblokRichTextDoc } from "../../.storyblok/types/storyblok";
import { convertSbPathToUrl } from "./convertSbPathToUrl";
import { isExternalLink } from "./isExternalLink";
import { replaceRedBullWhitespaces } from "./replaceRedBullWhitespaces";
import { defaultSchema } from "hast-util-sanitize";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import {
  MARK_LINK,
  RenderOptions,
  render as richTextRenderer,
} from "storyblok-rich-text-react-renderer";

// Define a type for your possible field values
type FieldType = string | StoryblokRichTextDoc | null | undefined;

// Extend rehypeSanitize defaultSchema to allow 'target' on 'a' elements
const customSanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    a: [...(defaultSchema.attributes?.a || []), "target"], // Allow 'target' attribute
  },
};

/**
 * Renders a Storyblok text field based on its type.
 * Converts Rich Text to HTML
 * Converts a String/Markdown to HTML
 */
export const renderSbText = (
  field: FieldType,
  localeString: string,
  blokResolvers?: RenderOptions["blokResolvers"],
): React.ReactNode | null => {
  // Handle null or undefined fields
  if (field === null || field === undefined) {
    return null;
  }

  // Check if it's a rich text field (usually has a type and content structure)
  if (
    typeof field === "object" &&
    "type" in field &&
    field.type === "doc" &&
    Array.isArray(field.content)
  ) {
    // Render rich text field using storyblok-rich-text-react-renderer
    return richTextRenderer(field, {
      markResolvers: {
        [MARK_LINK]: (children, props) => {
          const { linktype, href, target } = props;

          if (linktype === "email") {
            return <a href={`mailto:${href}`}>{children}</a>;
          }

          /* Internal links: map to <Link> */
          return (
            <a
              href={
                isExternalLink(href ?? "")
                  ? href
                  : convertSbPathToUrl(localeString, href ?? "")
              }
              target={target}
              rel={target === "_blank" ? "noopener noreferrer" : undefined}
            >
              {children}
            </a>
          );
        },
      },
      blokResolvers,
    });
  }

  // Render markdown field using react-markdown
  if (typeof field === "string") {
    return (
      <Markdown
        rehypePlugins={[rehypeRaw, [rehypeSanitize, customSanitizeSchema]]}
        urlTransform={(url) =>
          isExternalLink(url ?? "")
            ? url
            : convertSbPathToUrl(localeString, url)
        }
        children={replaceRedBullWhitespaces(field)}
        components={{
          a: ({ target, children, ...props }) => (
            <a
              {...props}
              target={target || undefined}
              rel={target === "_blank" ? "noopener noreferrer" : undefined}
            >
              {children}
            </a>
          ),
        }}
      />
    );
  }

  // If the data type is not supported
  return null;
};
